import { useId } from 'react';
import './mark.css';

/* --------------------------------------------------------------------------
   The 205 mark — Alabama wearing its own flag, with the area code beneath.

   ## The silhouette is real

   The outline is the actual state boundary, not a drawing of one. It comes
   from the US Census cartographic boundary file (`us-atlas` states-10m,
   public domain), decoded from its TopoJSON arcs and projected with a cosine
   correction at Alabama's mid-latitude (32.6°N), so the shape is not stretched
   the way a raw lon/lat plot stretches everything. Aspect is 1 : 1.5847,
   which is Alabama's real proportion.

   That matters more than it sounds. Everyone who will use this app lives in
   Alabama. A hand-drawn state — the Gulf coast too straight, the Mobile delta
   missing, the Chattahoochee's wander down the east side smoothed into a
   ruler line — reads as *wrong* immediately to someone from here, even if
   they cannot say why. The real boundary costs the same bytes.

   Dauphin Island is dropped. It is 0.04% of the state's area and at 34px it
   is one grey pixel adrift in the Gulf, which reads as dirt on the screen.

   ## The saltire runs corner to corner, and that is deliberate

   The state flag is a crimson St Andrew's cross on white — a *square* flag by
   the 1895 act. Drawn honestly at 45° inside a shape half as wide as it is
   tall, the arms leave through the sides at two-thirds height and the whole
   southern third of the state stays blank; the mark then reads as a small x
   floating in a tall box. Both were rendered and compared.

   So the bars run to the corners of the state's own bounding box, at 57.7°.
   This is the compromise every state-shaped Alabama badge makes, and it is
   the right one: the mark has to say *Alabama* at 34 pixels in a nav rail,
   and fidelity to the flag's geometry is worth less here than being legible.

   ## Alive

   Three motions, none of them a spinner:

   - the border **traces** itself, then the saltire **draws on**, one bar and
     then the other
   - a specular **sheen** crosses the state on a slow loop, the way light
     moves over enamel — this is what makes it read as an object and not a
     decal
   - the crimson **breathes** underneath, a slow bloom

   `205` arrives after the bars land, tightening as it settles.

   All of it collapses to the finished frame under `prefers-reduced-motion` —
   *finished*, not frozen. Someone who has asked for no motion should see the
   completed mark, never an empty outline waiting for an animation that will
   never run.
   ----------------------------------------------------------------------- */

/** Alabama, from the Census boundary. Box is 100 × 158.47. */
export const ALABAMA =
  'M7.62 0.43 L7.62 0 L22.65 0.17 L28.96 0.17 L44.99 0.54 L54.31 0.6 L58.02 0.54 L73.95 0.65 L80.06 0.8 L81.56 9.93 L83.87 22.55 L85.67 33.33 L86.97 41.7 L88.48 50.56 L91.78 70.85 L92.69 71.76 L92.28 72.79 L93.59 74.07 L94.09 78.31 L95.09 80.36 L96.79 82.33 L97.09 84.66 L98 85.69 L96.99 89.02 L98.8 89.81 L100 90.95 L99 92.43 L97.9 92.43 L98.1 93.11 L96.69 93.71 L95.39 95.19 L95.69 96.79 L95.49 99.24 L94.79 101.68 L93.39 103.73 L93.09 105.01 L93.69 108.54 L93.49 109.8 L95.39 112.27 L95.69 115.69 L94.99 117.31 L95.19 118.54 L94.39 120.81 L94.49 123.06 L93.79 123.66 L94.29 125.11 L93.99 126.65 L95.99 129.24 L96.69 131.06 L96.89 132.8 L90.18 132.8 L83.07 132.94 L72.04 133.05 L54.61 133.05 L41.08 132.85 L24.45 132.91 L24.75 133.99 L23.45 137.27 L23.65 137.92 L25.95 140.17 L26.25 141.34 L29.86 143.61 L30.16 145.58 L28.66 148.48 L28.96 150.05 L30.96 151.5 L29.16 152.5 L28.36 154.83 L27.45 155.26 L28.16 155.94 L26.65 156.68 L22.85 157.68 L18.84 158.36 L13.23 158.47 L15.03 157.31 L16.23 158.05 L19.74 157.28 L20.04 156.31 L18.54 154.94 L17.84 153.72 L15.83 152.38 L15.13 149.82 L16.03 147.71 L15.73 145.55 L15.03 144.18 L13.03 143.27 L11.52 144.58 L11.72 145.66 L10.82 147.29 L10.92 148.45 L10.32 149.37 L10.22 153.46 L9.42 155.34 L7.92 155.46 L8.02 154.43 L6.01 153.29 L4.51 153.75 L4.01 153.09 L2.2 153.72 L1.9 147.4 L1.6 137.24 L1 125.2 L0 103.19 L0.9 95.14 L3.01 74.92 L3.51 68.89 L6.81 37.01 L7.41 32.45 L9.32 14.15 L10.52 3.84 L8.92 2.85 L7.62 0.43 Z';

export const ALABAMA_W = 100;
export const ALABAMA_H = 158.47;

/* The bars overshoot the bounding box by 14% at each end so they are cut by
   the coastline rather than stopping just inside it — a bar that ends a
   hairline short of the border looks like a rendering bug at every size. */
const OVER = 0.14;
const X0 = -ALABAMA_W * OVER;
const X1 = ALABAMA_W * (1 + OVER);
const Y0 = -ALABAMA_H * OVER;
const Y1 = ALABAMA_H * (1 + OVER);
/** Length of one bar — the dash the draw-on animation runs down. */
const BAR = Math.hypot(X1 - X0, Y1 - Y0);

/** Height of the box when `205` is set beneath the state. */
const LABELLED_H = 205;

export interface MarkProps {
  /** Rendered height in px. Width follows the mark's own proportion. */
  size?: number;
  /** Trace, draw-on and sheen. `false` renders the finished frame at once. */
  animate?: boolean;
  /** Set `205` under the state. Off where a wordmark already says it. */
  label?: boolean;
  className?: string;
  /**
   * Accessible name. Give one only where the mark carries meaning on its own;
   * beside the words "205 Holdings" it is decoration and is hidden, because a
   * screen reader announcing "205 Holdings, 205 Holdings" is worse than
   * silence.
   */
  title?: string;
}

/**
 * `useId`, not a module counter: two marks on one page — the rail and the
 * sign-in card — must not share gradient or clip ids, and React's ids stay
 * stable across hydration in a way a counter does not.
 */
export function Mark({
  size = 60,
  animate = true,
  label = true,
  className,
  title,
}: MarkProps) {
  const uid = useId().replace(/:/g, '');
  const clip = `mk-clip-${uid}`;
  const sheen = `mk-sheen-${uid}`;
  const edge = `mk-edge-${uid}`;
  const bloom = `mk-bloom-${uid}`;
  const shade = `mk-shade-${uid}`;

  const boxH = label ? LABELLED_H : ALABAMA_H + 8;
  const boxW = ALABAMA_W + 8;

  return (
    <svg
      className={['mk', animate ? 'mk--live' : 'mk--still', className]
        .filter(Boolean)
        .join(' ')}
      viewBox={`-4 -4 ${boxW} ${boxH}`}
      style={{ height: size, width: (size * boxW) / boxH }}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <defs>
        <clipPath id={clip}>
          <path d={ALABAMA} />
        </clipPath>

        {/* `currentColor`, not white.
            The first cut hardcoded white, which is right on the near-black
            rail and invisible on the sign-in panel — a white silhouette
            outlined in white, so all anyone saw was a crimson X floating with
            no state around it. The stops inherit the SVG's `color`, which
            `mark.css` binds to `--text-hi`, so the edge is near-white in the
            dark theme and near-black in the light one.
            The falling opacity stays: it is what makes the edge read as a lit
            surface rather than a traced outline. */}
        <linearGradient id={edge} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.95" />
          <stop offset="55%" stopColor="currentColor" stopOpacity="0.62" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.85" />
        </linearGradient>

        {/* Depth. A flat crimson reads as sticker vinyl; this is a lit
            surface. It must be a gradient — the first cut used a half-height
            rectangle at 10% black and left a visible seam straight across
            the state. */}
        <linearGradient id={shade} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.10" />
          <stop offset="48%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.17" />
        </linearGradient>

        <linearGradient id={sheen} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        <radialGradient id={bloom} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="var(--crimson-lite, #E11D48)" stopOpacity="0.5" />
          <stop offset="100%" stopColor="var(--crimson-lite, #E11D48)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* The breath. Behind everything and unclipped — it is the glow the
          silhouette sits in, not part of the silhouette. */}
      <ellipse
        className="mk__bloom"
        cx={ALABAMA_W / 2}
        cy={ALABAMA_H / 2}
        rx={ALABAMA_W * 0.62}
        ry={ALABAMA_H * 0.52}
        fill={`url(#${bloom})`}
      />

      <g clipPath={`url(#${clip})`}>
        <rect
          x="-10" y="-10"
          width={ALABAMA_W + 20} height={ALABAMA_H + 20}
          fill="#F7F4F2"
        />

        {/* Butt caps: a round cap bulges past the coast and reads as a blob
            rather than a bar cut off by a border. */}
        <line
          className="mk__bar mk__bar--a"
          x1={X0} y1={Y0} x2={X1} y2={Y1}
          stroke="var(--crimson, #B20022)"
          strokeWidth="22"
          strokeLinecap="butt"
          style={{ strokeDasharray: BAR, strokeDashoffset: animate ? BAR : 0 }}
        />
        <line
          className="mk__bar mk__bar--b"
          x1={X0} y1={Y1} x2={X1} y2={Y0}
          stroke="var(--crimson, #B20022)"
          strokeWidth="22"
          strokeLinecap="butt"
          style={{ strokeDasharray: BAR, strokeDashoffset: animate ? BAR : 0 }}
        />

        <rect
          x="-10" y="-10"
          width={ALABAMA_W + 20} height={ALABAMA_H + 20}
          fill={`url(#${shade})`}
        />

        {/* Wider than the state and skewed, so it crosses as a band of light
            rather than a moving rectangle. */}
        <rect
          className="mk__sheen"
          x="-90" y="-30" width="70" height={ALABAMA_H + 60}
          fill={`url(#${sheen})`}
          transform="skewX(-18)"
        />
      </g>

      {/* Two strokes, both over the fill so the saltire is cut cleanly by the
          coastline.

          The keyline goes down first and is the wider of the two. It is what
          separates a near-white state from a near-white page; on the dark rail
          it is a shadow nobody notices. Without it the mark has no silhouette
          at all in the light theme, because the field (#F7F4F2) and the panel
          are within a few percent of each other. */}
      <path
        className="mk__keyline"
        d={ALABAMA}
        fill="none"
        stroke="#000"
        strokeOpacity="0.28"
        strokeWidth="4.2"
        strokeLinejoin="round"
      />
      <path
        className="mk__edge"
        d={ALABAMA}
        fill="none"
        stroke={`url(#${edge})`}
        strokeWidth="2.6"
        strokeLinejoin="round"
      />

      {label && (
        <text className="mk__205" x={ALABAMA_W / 2} y={ALABAMA_H + 40} textAnchor="middle">
          205
        </text>
      )}
    </svg>
  );
}

export default Mark;
